import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  Button,
  Typography,
  Chip,
  Stack
} from '@mui/material';
import { Check, Close, PersonAdd } from '@mui/icons-material';
import { formatDistanceToNow, parseISO } from 'date-fns';

const FriendRequests = ({ requests, onAccept, onDecline, loading }) => {
  const formatRequestTime = (timestamp) => {
    if (!timestamp) return '';
    
    try {
      const date = parseISO(timestamp);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
      return 'Recently';
    }
  };

  if (!requests || requests.length === 0) {
    return (
      <Box textAlign="center" py={6}>
        <PersonAdd sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No pending friend requests
        </Typography>
        <Typography variant="body2" color="text.secondary">
          When someone sends you a friend request, it will appear here
        </Typography>
      </Box>
    );
  }

  return (
    <List disablePadding>
      {requests.map((request) => {
        const user = request.from || request.fromUser || request.user;
        
        return (
          <ListItem key={request.id} divider>
            <ListItemAvatar>
              <Avatar 
                src={user?.avatar || user?.profilePicture}
                sx={{ bgcolor: 'primary.main' }}
              >
                {(user?.name || user?.display_name || user?.username || 'U').charAt(0).toUpperCase()}
              </Avatar>
            </ListItemAvatar>
            
            <ListItemText
              primary={
                <Typography variant="body1" fontWeight="medium">
                  {user?.name || user?.display_name || user?.username || 'Unknown User'}
                </Typography>
              }
              secondary={
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Wants to be your friend
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    {request.timestamp && (
                      <Chip 
                        label={formatRequestTime(request.timestamp)} 
                        size="small" 
                        variant="outlined"
                        color="primary"
                      />
                    )}
                    {user?.email && (
                      <Typography variant="caption" color="text.secondary">
                        {user.email}
                      </Typography>
                    )}
                  </Box>
                  {request.mutualFriends && request.mutualFriends > 0 && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {request.mutualFriends} mutual friend{request.mutualFriends !== 1 ? 's' : ''}
                    </Typography>
                  )}
                </Box>
              }
            />
            
            <ListItemSecondaryAction>
              <Stack direction="row" spacing={1}>
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  startIcon={<Check />}
                  onClick={() => onAccept(request.id)}
                  disabled={loading}
                >
                  Accept
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<Close />}
                  onClick={() => onDecline(request.id)}
                  disabled={loading}
                >
                  Decline
                </Button>
              </Stack>
            </ListItemSecondaryAction>
          </ListItem>
        );
      })}
    </List>
  );
};

export default FriendRequests;